import axios from 'axios';
const BASE_URL = '/charges';

// Helper to format date to ISO string (YYYY-MM-DD)
const formatDateForApi = date => {
  if (!date) return undefined;
  if (date instanceof Date) {
    return date.toNetworkDate();
  }
  return date;
};

// Helper to map filter parameters with fallback keys
const mapFilter = (params, ...keys) => {
  for (const key of keys) {
    if (params[key] !== undefined) {
      return params[key];
    }
  }
  return undefined;
};
const buildChargesTableParams = (params = {}) => {
  const apiParams = {
    // Pagination: API expects Offset and Limit
    Limit: params.pageSize || 50,
    Offset: params.page ? (params.page - 1) * (params.pageSize || 50) : 0,
    // Search - backend expects Filter.Search
    'Filter.Search': params['Filter.Search'] || params.search || undefined,
    // Sorting
    OrderBy: params.sortBy || params.orderBy || undefined,
    SortAscending: params.sortDirection === 'asc' ? true : params.sortAscending !== undefined ? params.sortAscending : undefined,
    // Filters - use helper for cleaner mapping
    'Filter.BilledState': mapFilter(params, 'billedState'),
    'Filter.CustomFlag': mapFilter(params, 'customFlag'),
    'Filter.ChargeCompleted': mapFilter(params, 'completionState', 'chargeCompleted'),
    'Filter.ChargeType': mapFilter(params, 'chargeType'),
    'Filter.MatterId': mapFilter(params, 'matterId'),
    'Filter.ClientId': mapFilter(params, 'clientId'),
    'Filter.AccountId': mapFilter(params, 'invoiceUserId', 'accountId'),
    'Filter.MatterTagId': mapFilter(params, 'practiceAreaId', 'matterTagId'),
    'Filter.ActivityCode': mapFilter(params, 'activityCode', 'activityCodeId'),
    'Filter.TaskCode': mapFilter(params, 'taskCode', 'taskCodeId'),
    // mapFilter keeps an explicit false — only undefined is stripped below, which
    // matters here because false means "Time Unlocked", not "no filter".
    'Filter.TimeLocked': mapFilter(params, 'timeLocked'),
    'Filter.ProfitabilityCategory': mapFilter(params, 'profitabilityCategory'),
    'Filter.ExcludeNoDate': mapFilter(params, 'excludeNoDate'),
    // Matters Involving Filter (from matters page API format)
    'Filter.ScopeAccount': mapFilter(params, 'scopeAccount'),
    'Filter.Originating': mapFilter(params, 'originating'),
    'Filter.Involved': mapFilter(params, 'involved'),
    'Filter.Primary': mapFilter(params, 'primary'),
    'Filter.Reviewer': mapFilter(params, 'reviewer'),
    // Date filters with formatting
    'Filter.StartDate': formatDateForApi(params.billableDateStart || params.startDate),
    'Filter.EndDate': formatDateForApi(params.billableDateEnd || params.endDate),
    'Filter.CreatedDateStart': formatDateForApi(params.createdDateStart),
    'Filter.CreatedDateEnd': formatDateForApi(params.createdDateEnd)
  };

  // Custom field filters
  Object.keys(params).forEach(key => {
    if (key.indexOf('customfield[') > -1) {
      apiParams[`Filter.${key}`] = params[key];
    }
  });

  // Remove undefined parameters
  Object.keys(apiParams).forEach(key => {
    if (apiParams[key] === undefined) {
      delete apiParams[key];
    }
  });
  return apiParams;
};
class ChargeService {
  // Load existing charge
  async getCharge(chargeId) {
    if (!chargeId) {
      throw new Error('Charge ID is required');
    }
    try {
      const response = await axios.get(`${BASE_URL}/${chargeId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to load charge:', error);
      throw error;
    }
  }
  async listChargesForTimeEntry(matterId) {
    if (!matterId) {
      throw new Error('Matter ID is required');
    }
    try {
      const response = await axios.get(`${BASE_URL}/list-charges-for-entry?matterId=${matterId}`);
      const charges = response.data || [];
      return charges.map(charge => ({
        ...charge,
        displayName: charge.displayName || charge.label || charge.description
      }));
    } catch (error) {
      console.error('Failed to load charges:', error);
      throw error;
    }
  }

  // Create new charge
  async createCharge(chargeData) {
    if (!chargeData) {
      throw new Error('Charge data is required');
    }
    try {
      const response = await axios.post(BASE_URL, chargeData);
      return response.data;
    } catch (error) {
      console.error('Failed to create charge:', error);
      throw error;
    }
  }

  // Create standard charge template
  async createStandardCharge(chargeData) {
    if (!chargeData) {
      throw new Error('Charge data is required');
    }
    try {
      const response = await axios.post(`${BASE_URL}/standard`, chargeData);
      return response.data;
    } catch (error) {
      console.error('Failed to create standard charge:', error);
      throw error;
    }
  }
  async createChargeWithType(chargeData, isStandardCharge) {
    if (isStandardCharge) {
      return this.createStandardCharge(chargeData);
    }
    return this.createCharge(chargeData);
  }

  // Update existing charge
  async updateCharge(chargeId, chargeData) {
    if (!chargeId) {
      throw new Error('Charge ID is required');
    }
    if (!chargeData) {
      throw new Error('Charge data is required');
    }
    try {
      const response = await axios.put(`${BASE_URL}/${chargeId}`, chargeData);
      return response.data;
    } catch (error) {
      console.error('Failed to update charge:', error);
      throw error;
    }
  }

  // Delete charge
  async deleteCharge(chargeId, force = false) {
    if (!chargeId) {
      throw new Error('Charge ID is required');
    }
    try {
      const response = await axios.delete(`${BASE_URL}/${chargeId}`, {
        params: {
          force
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to delete charge:', error);
      throw error;
    }
  }

  // Get deletion preview for multiple charges
  async getDeletionPreview(chargeIds) {
    if (!chargeIds || chargeIds.length === 0) {
      throw new Error('At least one charge ID is required');
    }
    try {
      const response = await axios.get(`${BASE_URL}/deletion-preview`, {
        params: {
          chargeIds
        },
        paramsSerializer: {
          indexes: null
        } // This creates chargeIds=x&chargeIds=y format
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get deletion preview:', error);
      throw error;
    }
  }

  // Bulk delete charges
  async bulkDeleteCharges(chargeIds, force = false) {
    if (!chargeIds || chargeIds.length === 0) {
      throw new Error('At least one charge ID is required');
    }
    try {
      const response = await axios.post(`${BASE_URL}/bulk-delete`, {
        ids: chargeIds,
        force
      });
      return response.data;
    } catch (error) {
      console.error('Failed to bulk delete charges:', error);
      throw error;
    }
  }

  // Delete recurring charge with mode
  // mode: 'ThisInstance' | 'ThisAndFutureInstances'
  async deleteRecurring(chargeId, mode, force = false) {
    if (!chargeId) {
      throw new Error('Charge ID is required');
    }
    if (!mode) {
      throw new Error('Delete mode is required');
    }
    try {
      const response = await axios.delete(`${BASE_URL}/${chargeId}/recurring`, {
        params: {
          mode,
          force
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to delete recurring charge:', error);
      throw error;
    }
  }

  // Bulk update completion state
  async bulkUpdateCompletion(chargeIds, completionState) {
    if (!chargeIds || chargeIds.length === 0) {
      throw new Error('At least one charge ID is required');
    }
    try {
      // completionState: 0 = NotCompleted, 1 = Completed
      const response = await axios.post(`${BASE_URL}/bulk-update-completion`, {
        ids: chargeIds,
        completionState
      });
      return response.data;
    } catch (error) {
      console.error('Failed to bulk update completion state:', error);
      throw error;
    }
  }

  // Bulk Lock / Unlock Time. A single fee is a list of one — there is no
  // single-fee endpoint, so the row actions this replaces have nothing to call.
  // Locking is two-pass (BR-012): without wipDisposition, a selection with WIP
  // writes nothing and returns { requiresConfirmation, totalWipEntryCount,
  // chargesWithWipEntries }; resend with the chosen wipDisposition to lock.
  // The disposition travels here — never via /entries/mark.
  async bulkUpdateLockStatus(chargeIds, timeLocked, wipDisposition = undefined) {
    if (!chargeIds || chargeIds.length === 0) {
      throw new Error('At least one charge ID is required');
    }
    const response = await axios.post(`${BASE_URL}/bulk-update-lock-status`, {
      ids: chargeIds,
      timeLocked,
      ...(wipDisposition ? {
        wipDisposition
      } : {})
    });
    return response.data;
  }

  // Load standard charges (templates)
  async loadStandardCharges() {
    try {
      const response = await axios.get(`${BASE_URL}/standard`);
      const charges = response.data.charges || [];
      return charges.sort((a, b) => {
        const aDisplay = a.label || a.description;
        const bDisplay = b.label || b.description;
        return aDisplay.localeCompare(bDisplay);
      });
    } catch (error) {
      console.error('Failed to load standard charges:', error);
      throw error;
    }
  }

  // List standard charges for select dropdowns
  async listStandardCharges() {
    try {
      const response = await axios.get(`${BASE_URL}/standard`);
      return response.data.select || [];
    } catch (error) {
      console.error('Failed to load standard charges:', error);
      throw error;
    }
  }

  // Load charges for a matter. `acceptsTime` subsumes `billed` rather than
  // entries to a charge, where a Billed + Time Unlocked fee must still show up.
  async listCharges(matterId, billed = false, acceptsTime = false) {
    if (!matterId) {
      throw new Error('Matter ID is required');
    }
    try {
      const query = acceptsTime ? `matterId=${matterId}&acceptsTime=true` : `matterId=${matterId}&billed=${billed}`;
      const response = await axios.get(`${BASE_URL}?${query}`);
      const charges = response.data.charges.map(({
        label,
        description,
        displayName,
        id
      }) => ({
        description: label || description,
        displayName: displayName || label || description,
        id
      })).sort((a, b) => (a.displayName || a.description).localeCompare(b.displayName || b.description));
      return charges;
    } catch (error) {
      console.error('Failed to load charges:', error);
      throw error;
    }
  }

  // Get all charges from /charges/table endpoint
  // This service layer maps frontend parameter names to backend API format (Filter.*, PascalCase)
  async getChargesTable(params = {}) {
    try {
      const apiParams = buildChargesTableParams(params);
      const response = await axios.get(`${BASE_URL}/table`, {
        params: apiParams
      });
      // API returns: { items: [...], count: number }
      return response.data;
    } catch (error) {
      console.error('Failed to load charges table:', error);
      throw error;
    }
  }
  exportChargesTableUrl(params = {}, leanLawAuth = {}) {
    const apiParams = buildChargesTableParams(params);
    const queryString = new URLSearchParams(apiParams).toString();
    const url = `${leanLawAuth.domain}/v1${BASE_URL}/table/export?${queryString}`;
    window.open(url, '_blank');
  }

  // Get time entries for a specific charge
  async getChargeTimeEntries(chargeId) {
    if (!chargeId) {
      throw new Error('Charge ID is required');
    }
    try {
      const response = await axios.get(`${BASE_URL}/${chargeId}/time-entries`);
      return response.data;
    } catch (error) {
      console.error('Failed to load charge time entries:', error);
      throw error;
    }
  }

  // Bulk convert time entries from fixed fee
  // targetBillingType: 'Billable' | 'NonBillable'
  async bulkConvertFromFixedFee(entryIds, targetBillingType) {
    if (!entryIds || !Array.isArray(entryIds) || entryIds.length === 0) {
      throw new Error('Entry IDs array is required');
    }
    if (!targetBillingType) {
      throw new Error('Target billing type is required');
    }
    try {
      const response = await axios.post('/entries/bulk-convert-from-fixedfee', {
        entryIds,
        targetBillingType
      });
      return response.data;
    } catch (error) {
      console.error('Failed to bulk convert entries from fixed fee:', error);
      throw error;
    }
  }

  // Bulk assign time entries to a fixed fee charge
  async bulkAssignEntriesToCharge(chargeId, entryIds, force = true) {
    if (!chargeId) {
      throw new Error('Charge ID is required');
    }
    if (!entryIds || !Array.isArray(entryIds) || entryIds.length === 0) {
      throw new Error('Entry IDs array is required');
    }
    try {
      const response = await axios.post(`${BASE_URL}/${chargeId}/bulk-assign-entries`, {
        ids: entryIds,
        force
      });
      return response.data;
    } catch (error) {
      console.error('Failed to bulk assign entries to charge:', error);
      throw error;
    }
  }

  // Bulk move fixed fee time entries to another matter
  // targetMatterId: string (GUID)
  async bulkMoveFixedFeeEntriesToMatter(entryIds, targetMatterId) {
    if (!entryIds || !Array.isArray(entryIds) || entryIds.length === 0) {
      throw new Error('Entry IDs array is required');
    }
    if (!targetMatterId) {
      throw new Error('Target matter ID is required');
    }
    try {
      const response = await axios.post('/entries/bulk-move-fixed-fee', {
        ids: entryIds,
        targetMatterId
      });
      return response.data;
    } catch (error) {
      console.error('Failed to bulk move fixed fee entries to matter:', error);
      throw error;
    }
  }

  // Bulk change matter for fixed fee charges
  // Returns: { success, requiresConfirmation, successCount, totalCount, totalUnbilledEntryCount, chargesWithUnbilledEntries, errors }
  async bulkChangeMatter(chargeIds, targetMatterId, confirmMoveEntries = false) {
    if (!chargeIds || !Array.isArray(chargeIds) || chargeIds.length === 0) {
      throw new Error('Charge IDs array is required');
    }
    if (!targetMatterId) {
      throw new Error('Target matter ID is required');
    }
    try {
      const response = await axios.post(`${BASE_URL}/bulk-change-matter`, {
        ids: chargeIds,
        targetMatterId,
        confirmMoveEntries
      });
      return response.data;
    } catch (error) {
      console.error('Failed to bulk change matter for charges:', error);
      throw error;
    }
  }
}
export default new ChargeService();